import { useEffect, useState } from "react";
import { useAppSelector } from "@/redux/features/hooks";
import { useGetOrdersQuery } from "@/redux/services/order/order.api";
import {
  Order,
  OrderStatus,
  OrderType,
} from "@/redux/services/order/order.type";
import OrderCard from "@/components/order/OrderCard";
import OrderStatusGroup from "@/components/order/OrderStatusGroup";
import OrderTableGroup from "@/components/order/OrderTableGroup";
import { OrderSkeleton } from "@/components/order/OrderSkeleton";
import Dropdown from "../ui/dropdown";
import Switch from "../ui/switch";
import { cn } from "@/lib/utils";
import { SearchInput } from "../SearchInput";

import { useOrderNotifications } from "@/hooks/useOrderNotifications";
import {
  DateScope,
  RoleOrderConfig,
  TabValue,
  useOrderFilters,
} from "@/hooks/useOrderFilters";
import { useOrderRealtime } from "@/hooks/useOrderRealtime";
import { useGetRestaurantTablesQuery } from "@/redux/services/table/table.api";
import { Roles } from "@/redux/services/user/user.type";
import { AlertCircle, Loader2, ShoppingCart, WifiOff } from "lucide-react";
import { notificationService } from "@/services/NotificationService";
import { notificationPollingService } from "@/services/NotificationPollingService";
import { Button } from "../ui/button";

interface OrderListProps {
  restaurantId: string;
  limit: number;
  page?: number;
  dateScope?: DateScope;
  onTotalCountChange?: (count: number) => void;
}

const statusTabs: { label: string; value: TabValue; kitchenLabel?: string }[] = [
  { label: "Toutes", value: null },
  { label: "En attente", value: "pending" },
  { label: "Acceptées", value: "accepted", kitchenLabel: "À préparer" },
  { label: "En préparation", value: "preparing" },
  { label: "Prêtes", value: "ready" },
  { label: "Servies", value: "served" },
  { label: "Récupérées", value: "picked_up" },
  { label: "Livrées", value: "delivered" },
  { label: "Annulées", value: "canceled" },
];

const ALL_STATUSES: TabValue[] = [
  null,
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "picked_up",
  "delivered",
  "canceled",
];

// The /orders page is where staff go to find *any* order — including ones not
// in their hub/POS "needs attention" lists — so it shows every status for every
// role by default, with no pre-applied paid filter. Staff narrow down with the
// filters themselves. (Kitchen-specific tab labels and grouping are handled
// separately, via the isKitchen checks below, not through this config.)
const getRoleOrderConfig = (): RoleOrderConfig => ({
  visibleStatuses: ALL_STATUSES,
  defaultStatuses: [],
});

// Action-first ordering for grouped display.
const STATUS_GROUP_ORDER: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "picked_up",
  "delivered",
  "canceled",
  "rejected",
];

const getEmptyStateCopy = (
  statusFilter: TabValue,
  hasSearch: boolean
): { title: string; subtitle: string } => {
  if (hasSearch) {
    return {
      title: "Aucune commande trouvée",
      subtitle: "Vérifiez le numéro et réessayez",
    };
  }
  switch (statusFilter) {
    case null:
      return {
        title: "Aucune commande",
        subtitle: "Les commandes apparaîtront ici",
      };
    case "pending":
      return { title: "Tout est à jour", subtitle: "Aucune commande en attente" };
    case "accepted":
      return { title: "Aucune commande acceptée", subtitle: "" };
    case "preparing":
      return {
        title: "Aucune commande en préparation",
        subtitle: "La cuisine respire un instant",
      };
    case "ready":
      return { title: "Aucune commande prête", subtitle: "" };
    case "served":
      return { title: "Aucune commande servie", subtitle: "" };
    case "picked_up":
      return { title: "Aucune commande récupérée", subtitle: "" };
    case "delivered":
      return { title: "Aucune commande livrée", subtitle: "" };
    case "canceled":
      return { title: "Aucune commande annulée", subtitle: "" };
    default:
      return {
        title: "Aucune commande",
        subtitle: "Aucun résultat pour ce filtre",
      };
  }
};

const OrdersContent = ({
  isLoading,
  isError,
  onRetry,
  orders,
  searchOrderNumber,
  statusFilter,
  grouped,
  groupByTable,
  userRole,
  onModalOpen,
  onModalClose,
}: {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  orders: Order[];
  searchOrderNumber: string;
  statusFilter: TabValue;
  grouped: boolean;
  groupByTable: boolean;
  userRole?: Roles;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}) => {
  if (isLoading) {
    return <OrderSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <AlertCircle className="h-12 w-12 mb-3 text-destructive/70" />
        <p className="text-lg font-medium text-card-foreground">
          Impossible de charger les commandes
        </p>
        <p className="text-sm mb-4">Vérifiez votre connexion puis réessayez</p>
        <Button variant="outline" onClick={onRetry}>
          Réessayer
        </Button>
      </div>
    );
  }

  if (orders.length === 0) {
    const { title, subtitle } = getEmptyStateCopy(
      statusFilter,
      searchOrderNumber !== ""
    );
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <ShoppingCart className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-lg font-medium">{title}</p>
        {subtitle && <p className="text-sm">{subtitle}</p>}
      </div>
    );
  }

  if (groupByTable) {
    const byTable = new Map<string, Order[]>();
    const takeaway: Order[] = [];
    const delivery: Order[] = [];
    const other: Order[] = [];

    orders.forEach((order) => {
      if (order.restaurantTable?.code) {
        const code = order.restaurantTable.code;
        const existing = byTable.get(code);
        if (existing) existing.push(order);
        else byTable.set(code, [order]);
      } else if (order.orderType === "takeaway") {
        takeaway.push(order);
      } else if (order.orderType === "delivery") {
        delivery.push(order);
      } else {
        other.push(order);
      }
    });

    const sortedTableCodes = Array.from(byTable.keys()).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );

    return (
      <div className="space-y-6">
        {sortedTableCodes.map((code) => (
          <OrderTableGroup
            key={`t-${code}`}
            label={`Table ${code}`}
            kind="table"
            orders={byTable.get(code) ?? []}
            searchOrderNumber={searchOrderNumber}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
          />
        ))}
        {takeaway.length > 0 && (
          <OrderTableGroup
            label="À emporter"
            kind="takeaway"
            orders={takeaway}
            searchOrderNumber={searchOrderNumber}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
          />
        )}
        {delivery.length > 0 && (
          <OrderTableGroup
            label="Livraison"
            kind="delivery"
            orders={delivery}
            searchOrderNumber={searchOrderNumber}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
          />
        )}
        {other.length > 0 && (
          <OrderTableGroup
            label="Sans table"
            kind="other"
            orders={other}
            searchOrderNumber={searchOrderNumber}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
          />
        )}
      </div>
    );
  }

  if (grouped) {
    const groups = new Map<OrderStatus, Order[]>();
    orders.forEach((order) => {
      const existing = groups.get(order.status);
      if (existing) {
        existing.push(order);
      } else {
        groups.set(order.status, [order]);
      }
    });

    const orderedStatuses = STATUS_GROUP_ORDER.filter((s) => groups.has(s));
    // Keep any unexpected status at the end so we never silently drop orders.
    groups.forEach((_, status) => {
      if (!orderedStatuses.includes(status)) orderedStatuses.push(status);
    });

    return (
      <div className="space-y-6">
        {orderedStatuses.map((status) => (
          <OrderStatusGroup
            key={status}
            status={status}
            orders={groups.get(status) ?? []}
            searchOrderNumber={searchOrderNumber}
            userRole={userRole}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {orders.map((order: Order) => (
        <OrderCard
          key={order.id}
          order={order}
          searchOrderNumber={searchOrderNumber}
          onModalOpen={onModalOpen}
          onModalClose={onModalClose}
        />
      ))}
    </div>
  );
};

const OrdersList: React.FC<OrderListProps> = ({
  restaurantId,
  limit,
  page = 1,
  dateScope = "all",
  onTotalCountChange,
}) => {
  const { currentRestaurant, currentUser } = useAppSelector(
    (state) => state.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const isKitchen = currentUser?.roles?.includes(Roles.kitchen);
  const roleConfig = getRoleOrderConfig();
  const { data: tables } = useGetRestaurantTablesQuery(
    { restaurantId: currentRestaurant?.id },
    { skip: isOwner }
  );

  const {
    statusFilter,
    tableFilter,
    orderTypeFilter,
    paidFilter,
    groupByTable,
    searchOrderNumber,
    setStatusFilter,
    setTableFilter,
    setOrderTypeFilter,
    setPaidFilter,
    setGroupByTable,
    setSearchOrderNumber,
    queryParams,
    canTogglePaid,
    isFilterChange,
    acknowledgeChange,
  } = useOrderFilters({
    limit,
    page,
    currentUserRoles: currentUser?.roles,
    roleConfig,
    dateScope,
  });

  const {
    data: initialOrders,
    isLoading: isLoadingOrders,
    isFetching: isFetchingOrders,
    isError: isOrdersError,
    refetch: refetchOrders,
  } = useGetOrdersQuery({ restaurantId, query: queryParams });

  const { orders, sortedOrders, onDrawerOpen, onDrawerClose } =
    useOrderRealtime({
      restaurantId,
      page,
      initialData: initialOrders,
      onInitialDataLoaded: (count) => {
        onTotalCountChange?.(count);
        acknowledgeChange();
      },
    });

  const [isConnectionDown, setIsConnectionDown] = useState(false);

  useEffect(() => {
    const check = () => {
      const mercure = notificationService.getConnectionStatus();
      const polling = notificationPollingService.isActive();
      setIsConnectionDown(
        !mercure.connected && !mercure.isServerConnectionAlive && !polling
      );
    };
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, []);

  const { hasPendingOrders, pendingOrdersCount } = useOrderNotifications(
    orders,
    30000
  );

  return (
    <div className="space-y-5">
      {isConnectionDown && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-destructive">
          <div className="flex items-center gap-2">
            <WifiOff className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">
              Connexion temps réel perdue. Les commandes peuvent ne pas être à
              jour.
            </p>
          </div>
          <button
            type="button"
            onClick={() => notificationService.forceReset()}
            className="text-sm font-medium underline whitespace-nowrap"
          >
            Reconnecter
          </button>
        </div>
      )}

      {/* Filters bar */}
      <div className="space-y-3">
        {/* Status tabs */}
        <div
          className="flex items-center gap-2 overflow-x-auto pb-1 scroll-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, black calc(100% - 32px), transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, black calc(100% - 32px), transparent)",
          }}
        >
          {statusTabs.filter((tab) => roleConfig.visibleStatuses.includes(tab.value)).map((tab) => (
            <button
              key={tab.value ?? "all"}
              onClick={() => setStatusFilter(tab.value)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                statusFilter === tab.value
                  ? "bg-primary text-white"
                  : "bg-muted text-muted-foreground hover:bg-border"
              )}
            >
              {isKitchen && tab.kitchenLabel ? tab.kitchenLabel : tab.label}
              {tab.value === "pending" && hasPendingOrders && (
                <span className="ml-1"> ({pendingOrdersCount})</span>
              )}
            </button>
          ))}
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-3 flex-wrap">
            <SearchInput
              placeholder="N° commande"
              value={searchOrderNumber}
              onChange={setSearchOrderNumber}
              className="w-[300px] flex-shrink-0"
            />
            {!isOwner && (
              <Dropdown
                items={
                  tables?.results?.map((table) => ({
                    label: table.code,
                    value: table.code,
                  })) ?? []
                }
                onSelect={(value) => setTableFilter(value as string)}
                buttonLabel="Table"
                variant="outline"
                showSelectedValue={true}
                selectedValue={tableFilter}
                includeClearOption={true}
                clearOptionLabel="Toutes les tables"
              />
            )}
            <Dropdown
              items={[
                { label: "Sur place", value: "dine_in" },
                { label: "À emporter", value: "takeaway" },
                { label: "Livraison", value: "delivery" },
              ]}
              onSelect={(value) =>
                setOrderTypeFilter(value as OrderType | null)
              }
              buttonLabel="Type"
              variant="outline"
              showSelectedValue={true}
              selectedValue={orderTypeFilter}
              includeClearOption={true}
              clearOptionLabel="Tous les types"
            />
            {canTogglePaid && (
              <Dropdown
                items={[
                  { label: "Payées", value: "paid" },
                  { label: "Non payées", value: "unpaid" },
                ]}
                onSelect={(value) =>
                  setPaidFilter(value as "paid" | "unpaid" | null)
                }
                buttonLabel="Paiement"
                variant="outline"
                showSelectedValue={true}
                selectedValue={paidFilter}
                includeClearOption={true}
                clearOptionLabel="Tous les paiements"
              />
            )}
          </div>

        {/* Group-by-table switch: only when no table filter and dine-in (or all types) */}
        {!tableFilter &&
          (orderTypeFilter === null || orderTypeFilter === "dine_in") && (
            <div className="flex items-center">
              <Switch
                id="group-by-table"
                label="Grouper par table"
                checked={groupByTable}
                onChange={setGroupByTable}
              />
            </div>
          )}
      </div>

      {/* Orders list */}
      <div className="relative">
        {isFilterChange.current && isFetchingOrders && !isLoadingOrders && (
          <div className="sticky top-1/2 z-10 flex justify-center pointer-events-none h-0">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        <div className={cn(isFilterChange.current && isFetchingOrders && !isLoadingOrders && "opacity-50 pointer-events-none transition-opacity")}>
          <OrdersContent
            isLoading={isLoadingOrders}
            isError={isOrdersError}
            onRetry={refetchOrders}
            orders={sortedOrders}
            searchOrderNumber={searchOrderNumber}
            statusFilter={statusFilter}
            grouped={statusFilter === null}
            groupByTable={groupByTable && !tableFilter}
            userRole={isKitchen ? Roles.kitchen : undefined}
            onModalOpen={onDrawerOpen}
            onModalClose={onDrawerClose}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersList;
